import { Lock, Sparkles, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { ToolCallMessagePartProps } from "@assistant-ui/react";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import { ClaimTrialButton } from "@/src/pages/trial-gate/Claim";
import upgradeGuyUrl from "@/images/upgrade_guy.png";
import extLogoUrl from "@/images/extlogo.png";

const upgradeGuyImgSrc = new URL(upgradeGuyUrl, import.meta.url).href;
const extLogoImgSrc = new URL(extLogoUrl, import.meta.url).href;

type TrialGateType = "chat" | "highlights";

type TrialGatePeriod = "hourly" | "daily" | "weekly" | "monthly" | "indefinite";

type TrialGateResult = {
  type: TrialGateType;
  limit_reached: boolean;
  current_usage: number;
  limit: number;
  period: TrialGatePeriod;
};

const PERIOD_TEXT = {
  hourly: {
    used: "this hour",
    resets: "Your limit resets at the top of each hour",
  },
  daily: {
    used: "today",
    resets: "Your limit resets daily at midnight",
  },
  weekly: {
    used: "this week",
    resets: "Your limit resets every Monday",
  },
  monthly: {
    used: "this month",
    resets: "Your limit resets on the 1st of each month",
  },
  indefinite: {
    used: "for now",
    resets: "Upgrade to Premium for unlimited access",
  },
} as const;

const GATE_CONFIG = {
  chat: {
    icon: Zap,
    title: "Get unlimited chats",
    getDescription: (period: TrialGatePeriod) =>
      `You've used all your free AI chat messages ${PERIOD_TEXT[period].used}.`,
    features: [
      "Unlimited BetterCampus AI chat messages",
      "Unlimited file uploads",
      "Upload larger files",
    ],
  },
  highlights: {
    icon: Zap,
    title: "Get unlimited study materials",
    getDescription: (period: TrialGatePeriod) =>
      `You've generated all your free study materials ${PERIOD_TEXT[period].used}.`,
    features: [
      "Unlimited flashcards and quizzes",
      "Longer and more detailed materials",
      "Unlimited file uploads",
    ],
  },
} as const;

export const TrialGateToolResult: React.FC<ToolCallMessagePartProps> = (
  props
) => {
  //   const result = props.result as TrialGateResult | undefined;

  //   if (!result) return <ToolFallback {...props} />;

  const config = GATE_CONFIG[props.args.type];
  const period = props.args.period || "indefinite";
  const Icon = config.icon;

  return (
    <div className="rounded-2xl border-2 border-orange-500 shadow-xl overflow-hidden mb-5 bg-theme-0">
      {/* Header */}
      <div className="px-6 py-5 bg-orange-500/10 border-b-2 border-orange-500">
        <div className="flex items-center gap-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl text-white">
            {/* <Sparkles className="h-6 w-6" /> */}
            <img src={extLogoImgSrc} />
          </div>

          <div className="flex-1">
            <div
              className="text-2xl font-bold text-theme-0"
              style={{ fontFamily: "var(--font-serif) !important" }}
            >
              {config.title} with{" "}
              <span
                className="underline decoration-wavy decoration-orange-600 decoration-2 underline-offset-4"
                style={{ fontFamily: "var(--font-serif) !important" }}
              >
                Learn+
              </span>
            </div>
            <img
              src={upgradeGuyImgSrc}
              className="size-[140px] object-contain absolute bottom-0 left-0"
            />
            <div className="text-sm font-medium text-theme-1 mt-0.5">
              {/* X / Y used {PERIOD_TEXT[period].used} */}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 bg-theme-0">
        <p className="text-sm text-theme-1 mb-4">
          {config.getDescription(period)}
        </p>

        {/* Features List */}
        <div className="space-y-2 mb-6">
          <p className="text-sm font-semibold text-theme-0 mb-3">
            Upgrade to{" "}
            <span className="underline decoration-wavy decoration-orange-600 decoration-2 underline-offset-4">
              Learn+
            </span>{" "}
            for:
          </p>
          {config.features.map((feature, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-sm text-theme-1"
            >
              <Icon className="h-4 w-4 text-orange-600 flex-shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col gap-3 px-30">
          {/* <button
            className={cn(
              "flex-1 px-4 py-3 rounded-lg font-semibold text-sm",
              "bg-brand-gradient text-white",
              "hover:opacity-80",
              "transition-opacity duration-200",
              "shadow-md hover:shadow-lg"
            )}
            onClick={() => {
              // Handle upgrade action
              console.log("Upgrade clicked");
            }}
          >
            Upgrade to Learn+
          </button> */}

          <ClaimTrialButton className="flex-1" />

          {/* <a
            className={cn(
              "flex-1 px-4 py-3 rounded-lg font-semibold text-sm",
              "bg-theme-0 text-theme-1 border-2 border-theme",
              "hover:bg-theme-2",
              "transition-colors duration-200 hover:cursor-pointer text-center"
            )}
            onClick={() => {
              // Handle learn more action
              console.log("Learn more clicked");
            }}
            // href="/pricing"
            target="_blank"
          >
            Learn More
          </a> */}
        </div>
        <div className="mt-1 w-full text-center hover:cursor-pointer hover:underline">
          <a
            className={cn("flex-1 text-xs", "text-theme-1", "text-center")}
            href="https://bettercampus.com/pricing"
            target="_blank"
          >
            View all features
          </a>
        </div>

        {/* Help text */}
        {/* <p className="text-xs text-theme-2 text-center mt-4">
          Removing materials, chats, or files will not increase your limits.
        </p> */}
      </div>
    </div>
  );
};
